import React, { useEffect } from 'react';
import { throttle } from 'lodash';
import PageHead from '@utils/PageHead';
import { Box, Grid, Palette, useTheme } from '@mui/material';
import { useLayoutStore } from '@store/LayoutStore';
import { SideBarDrawer, SideBarDrawerProps } from './SideBarDrawer';
import { Button } from '@components/button';
import { useAlertStore } from '@store/AlertStore';
import { useGetScreenSize } from '@utils/useGetScreenSize';
import { AlertStatus } from '@models/alert';
import { Alert } from '@components/alert/Alert';
import { HolidayModeControl } from './HolidayModeControl';
import { getTodaysHoliday } from '@theme/palettes/getPalette';
import { useApiEnvStore } from '@utils/api-client';
import { shallow } from 'zustand/shallow';

interface ILayout {
  title: string;
  children?: React.ReactNode;
  customHeader?: React.ReactElement;
  enableDropShadow?: boolean;
  showDropShadow?: boolean;
  sideDrawerProps?: SideBarDrawerProps;
  fullscreenMode?: boolean;
  /** An optional banner to appear under the header but above the side | main content */
  banner?: {
    text: string;
    type?: AlertStatus;
    onClick?: () => void;
    icon?: React.ReactNode;
  };
  banners?: {
    text: string;
    type?: AlertStatus;
    onClick?: () => void;
    icon?: React.ReactNode;
  }[];
  config?: {
    /** Optionally set the padding for the page.
     *  If your page needs to render a banner or something the entire width, set this to 0 and control padding on your */
    padding?: number | { xs: string | number; sm: string | number };
    disableScroll?: boolean;
    hideScrollUnlessHover?: boolean;
    /** Used to not use gradient in dark mode */
    noDarkGradient?: boolean;
    /** Used  to handle overflow and scrollbar behavior when fullscreen mode is enabled */
    fullscreenMode?: boolean;
  };
}

export const Layout = ({
  children,
  enableDropShadow,
  showDropShadow,
  config,
  customHeader,
  title,
  banner,
  banners,
  sideDrawerProps,
}: ILayout) => {
  const theme: any = useTheme();
  const [disableHolidayModeFlag] = useApiEnvStore((state) => [state.disableHolidayModeFlag], shallow);

  const activeHoliday = getTodaysHoliday();
  const overrideSticky = activeHoliday && activeHoliday !== 'Easter' && !disableHolidayModeFlag;

  const [isSticky, setIsSticky] = React.useState(
    overrideSticky
      ? true
      : ['halloweenPalette', 'aprilFoolsPalette'].includes(theme.palette.theme) && title !== 'Home'
        ? true
        : false,
  );

  const { isMobileScreen } = useGetScreenSize();
  const { headerHeight } = useAlertStore();
  const {
    showHeaderMenu,
    customHeaderRenderedInLayout,
    setCustomHeaderRenderedInLayout,
    setHighlightedNavigationElementId,
    highlightedNavigationElement,
  } = useLayoutStore(
    (state) => ({
      showHeaderMenu: state.showHeaderMenu,
      customHeaderRenderedInLayout: state.customHeaderRenderedInLayout,
      setCustomHeaderRenderedInLayout: state.setCustomHeaderRenderedInLayout,
      setHighlightedNavigationElementId: state.setHighlightedNavigationElementId,
      highlightedNavigationElement: state.highlightedNavigationElement,
    }),
    shallow,
  );

  const [isSidebarOpen] = useLayoutStore((state) => [state.isSidebarOpen, state.sidebarWidth], shallow);

  useEffect(() => {
    if (customHeaderRenderedInLayout !== !!customHeader) {
      setCustomHeaderRenderedInLayout(!!customHeader);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customHeader]);

  const findLastScrolledElement = (e: any) => {
    if (enableDropShadow) {
      setIsSticky(e.target.scrollTop >= headerHeight - 10);
    }

    // Only process scroll detection if sidebar is open and we need to track elements
    if (!isSidebarOpen || !sideDrawerProps?.classNameToFindLastScrolled) {
      return;
    }

    const elements = document.querySelectorAll(`.${sideDrawerProps?.classNameToFindLastScrolled}`);

    let lastScrolledElement: Element | undefined;
    let maxTop = -Infinity;

    elements.forEach((element) => {
      const rect = element.getBoundingClientRect();
      const topPosition = rect.top;

      // Check if the element's top is less than or equal to 150px from the top of the viewport
      if (topPosition <= 150 && topPosition > maxTop) {
        maxTop = topPosition;
        lastScrolledElement = element;
      }
    });

    // Only update if there's actually a meaningful change
    const newElementId = lastScrolledElement?.id ?? null;
    if (highlightedNavigationElement !== newElementId && newElementId !== null) {
      setHighlightedNavigationElementId(newElementId);
    }
  };

  const throttleFindLastScrolledElement = throttle(findLastScrolledElement, 50, {
    leading: true,
    trailing: true,
  });

  const forceThemeDropShadow = showDropShadow || theme.palette.theme === 'trueDark';

  return (
    <>
      <PageHead title={title} />
      <Grid
        container
        direction='column'
        flexWrap='nowrap'
        sx={{
          overflow: 'hidden !important',
          background: ({ palette }: { palette: any }) =>
            palette.mode === 'dark' && !config?.noDarkGradient ? palette.background.gradient : palette.background.main,
          height: '100vh',
        }}
      >
        {customHeader && <Box sx={{ width: '100vw', position: 'relative', left: 0, right: 0 }}>{customHeader}</Box>}
        <Box
          sx={{
            position: 'static',
            height: '9px',
            marginTop: '-10px',
            transition: 'all 200ms',
            borderRadius: 0,
            zIndex: 10,
            background: 'transparent',
            boxShadow:
              forceThemeDropShadow || (isSticky && !isMobileScreen)
                ? '0px 8px 4px -4px rgba(0,0,0,0.2),0px 4px 5px 0px rgba(0,0,0,0.14),0px 1px 10px 0px rgba(0,0,0,0.12)'
                : 'none',
          }}
        />

        {banner &&
          (banner.onClick ? (
            <Grid id='layout-banner' sx={sxStyles.modifyEndorsementDetailsBox}>
              <Button
                variant='text'
                onClick={banner.onClick}
                startIcon={banner.icon}
                sx={sxStyles.modifyEndorsementButton as any}
              >
                {banner.text}
              </Button>
            </Grid>
          ) : (
            banner.type && (
              <Alert type={banner.type} ctaFunction={banner.onClick}>
                {banner.text}
              </Alert>
            )
          ))}
        {banners &&
          banners.length > 0 &&
          banners.map((bnr, index) =>
            bnr.onClick ? (
              <Grid key={index} id='layout-banner' sx={sxStyles.modifyEndorsementDetailsBox}>
                <Button
                  variant='text'
                  onClick={bnr.onClick}
                  startIcon={bnr.icon}
                  sx={sxStyles.modifyEndorsementButton as any}
                >
                  {bnr.text}
                </Button>
              </Grid>
            ) : (
              bnr.type && (
                <Alert key={index} type={bnr.type} ctaFunction={bnr.onClick}>
                  {bnr.text}
                </Alert>
              )
            ),
          )}
        {!showHeaderMenu && ( // this is really "mobile menu open"
          <Grid
            id='layout-container'
            sx={{
              zIndex: 0,
              display: 'flex',
              flexGrow: 1,
              flexDirection: 'row', // Ensure sidebar and main content are siblings in a row
              padding: 0,
              marginTop: isSticky && !isMobileScreen ? '4px' : 0,
              height: '100%',
              overflowY: 'hidden', // Prevent overflow, let main content handle scrolling
              overscrollBehaviorBlock: 'contain',
            }}
          >
            {sideDrawerProps && (
              <Box
                sx={{
                  height: '100%',
                  transition: 'width 0.2s',
                  display: 'flex',
                  flexDirection: 'row',
                }}
              >
                <SideBarDrawer {...sideDrawerProps} />
              </Box>
            )}
            <Box
              component='main'
              onScroll={throttleFindLastScrolledElement}
              sx={{
                display: 'flex',
                flexDirection: 'column',
                height: '100%',
                flex: 1,
                maxWidth: '100%',
                overflowX: config?.fullscreenMode ? 'none' : { xs: 'hidden', sm: 'auto' },
                overflowY: config?.hideScrollUnlessHover || config?.disableScroll ? 'hidden' : 'auto',
                overscrollBehaviorBlock: 'contain',
                scrollbarColor: 'grey #FFFFFF05',
                scrollbarGutter:
                  config?.disableScroll || !config?.hideScrollUnlessHover ? undefined : { xs: 'unset', sm: 'stable' },

                ':hover': {
                  overflowY: config?.disableScroll ? 'hidden' : config?.hideScrollUnlessHover ? 'scroll' : 'auto',
                },
                p: config?.padding !== undefined ? config.padding : { xs: 0, sm: 1 },
                margin: 0,
                transition: 'all 0.2s ease',
              }}
            >
              {children}
            </Box>
          </Grid>
        )}
        <HolidayModeControl />
      </Grid>
    </>
  );
};

// moving to an object to help keep flow readable above
const sxStyles = {
  modifyEndorsementDetailsBox: {
    display: 'block',
    backgroundColor: 'primary.dark',
    boxShadow: 'boxShadow[3]',
    padding: '3px 0px 3px 15px',
    height: 'fit-content',
    zIndex: 2,
  },
  modifyEndorsementButton: {
    '&.MuiButton-root': {
      color: ({ palette }: { palette: Palette }) => `${palette.common.white} !important`,
    },
  },
};
